import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { MembersService } from '../services/members.service';

type MembersQueryKey = readonly ['members', number, number, string];

export interface UseMembersOptions {
  initialPage?: number;
  initialLimit?: number;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
}

export const getMembersQueryKey = (page: number, limit: number, search: string) => {
  return ['members', page, limit, search] as MembersQueryKey;
};

export const useMembers = (options: UseMembersOptions = {}) => {
  const {
    initialPage = 1,
    initialLimit = 12,
    staleTime = 0,
    refetchOnWindowFocus = false,
    refetchOnMount = true,
  } = options;

  const queryClient = useQueryClient();
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Use a ref to track the current page to avoid closure issues
  const currentPageRef = useRef(page);
  currentPageRef.current = page;

  // Base query key without page for invalidation
  const baseQueryKey = ['members'];

  // Create a fetch function that we can reuse
  const fetchMembers = useCallback(async (pageToFetch: number, limitToUse: number, searchTerm: string) => {
    setLoading(true);
    try {
      console.log(`Fetching members for page ${pageToFetch}`);
      return await MembersService.getMembers(pageToFetch, limitToUse, searchTerm);
    } finally {
      setLoading(false);
    }
  }, []);

  // Main query
  const {
    data,
    isLoading: isQueryLoading,
    error,
  } = useQuery({
    queryKey: getMembersQueryKey(page, limit, search),
    queryFn: () => fetchMembers(page, limit, search),
    staleTime,
    refetchOnWindowFocus,
    refetchOnMount,
  });

  // Refresh mutation
  const { mutate: refresh, isPending: isRefreshing } = useMutation({
    mutationFn: () => fetchMembers(currentPageRef.current, limit, search),
    onSuccess: (newData) => {
      queryClient.setQueryData(
        getMembersQueryKey(currentPageRef.current, limit, search),
        newData
      );
    },
  });

  // Handle search
  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPage(1);
  }, []);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    // Prevent redundant page changes
    if (newPage === currentPageRef.current) return;

    // Invalidate the current query before changing the page
    queryClient.invalidateQueries({
      queryKey: baseQueryKey,
      exact: false,
      refetchType: 'none',
    });

    // Update the page state
    setPage(newPage);
  }, [queryClient, baseQueryKey]);

  return {
    members: data?.members ?? [],
    isLoading: isQueryLoading || isRefreshing || loading,
    error: error?.message ?? null,
    page,
    limit,
    total: data?.total ?? 0,
    handleSearch,
    handlePageChange,
    refresh
  };
};
